import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { useDeepLinking } from "@/hooks/useDeepLinking";
import { AppLayout } from "@/components/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Coaches from "@/pages/Coaches";
import CoachDetails from "@/pages/CoachDetails";
import Classes from "@/pages/Classes";
import Schedule from "@/pages/Schedule";
import Attendance from "@/pages/Attendance";
import Settings from "@/pages/Settings";
import Inventory from "@/pages/Inventory";
import ClientDetails from "@/pages/ClientDetails";
import ClientsImport from "@/pages/admin/ClientsImport";
import Payments from "@/pages/Payments";
import SubscriptionTypes from "@/pages/admin/SubscriptionTypes";
import TrialsPage from "@/pages/admin/TrialsPage";
import CalendarsPage from "@/pages/admin/CalendarsPage";
import SessionDebts from "@/pages/admin/SessionDebts";
import Accounting from "@/pages/admin/Accounting";
import NotFound from "@/pages/NotFound";
import Login from "@/pages/Login";
import AthleteDashboard from "@/pages/AthleteDashboard";
import AthleteSchedule from "@/pages/athlete/AthleteSchedule";
import AthleteHistory from "@/pages/athlete/AthleteHistory";
import AthleteReservations from "@/pages/athlete/AthleteReservations";
import AthleteStats from "@/pages/athlete/AthleteStats";
import AthleteSubscriptions from "@/pages/athlete/AthleteSubscriptions";
import { AuthGuard } from "@/components/auth/AuthGuard";
import SetPasswordPage from "@/pages/SetPassword";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import UserProfile from "@/pages/UserProfile";
import { PublicLayout } from "@/components/layout/PublicLayout";
import OpenAppPage from "@/pages/OpenAppPage";
import TrialBookingPage from "@/pages/public/TrialBookingPage";

import { AdminRatingsPage } from "@/pages/admin/AdminRatingPage";
import AuditLogPage from "@/pages/AuditLogPage";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachPerformanceDashboard from "@/components/coaches/CoachPerformanceDashboard";
import { SuperAdminGuard } from "@/components/auth/SuperAdminGuard";
import SuperAdminDashboard from "@/pages/SuperAdmin/Dashboard";
import SuperAdminTenants from "@/pages/SuperAdmin/Tenants";
import SuperAdminAdmins from "@/pages/SuperAdmin/Admins";
import SuperAdminUsers from "@/pages/SuperAdmin/Users";
import SuperAdminActivity from "@/pages/SuperAdmin/Activity";
import SuperAdminHealth from "@/pages/SuperAdmin/Health";
import SuperAdminSessionsAudit from "@/pages/SuperAdmin/SessionsAudit";
import SuperAdminCancellationsReview from "@/pages/SuperAdmin/CancellationsReview";
import SuperAdminAppVersions from "@/pages/SuperAdmin/AppVersions";
import SuperAdminWelcomeMessages from "@/pages/SuperAdmin/WelcomeMessages";
import { AdminWelcomeGate } from "@/components/admin/AdminWelcomeGate";
import { MobileBrowserBlocker } from "@/components/guards/MobileBrowserBlocker";

const AdminLayout = () => {
  return (
    <MobileBrowserBlocker>
      <AuthGuard requiredRole="admin">
        <AppLayout isAdmin={true} isAthlete={false}>
          <AdminWelcomeGate />
          <Outlet />
        </AppLayout>
      </AuthGuard>
    </MobileBrowserBlocker>
  );
};

const CoachLayout = () => {
  return (
    <MobileBrowserBlocker>
      <AuthGuard requiredRole="coach">
        <AppLayout isAdmin={false} isAthlete={false} isCoach={true}>
          <Outlet />
        </AppLayout>
      </AuthGuard>
    </MobileBrowserBlocker>
  );
};

const AthleteLayout = () => {
  return (
    <MobileBrowserBlocker>
      <AuthGuard requiredRole="athlete">
        <AppLayout isAdmin={false} isAthlete={true}>
          <Outlet />
        </AppLayout>
      </AuthGuard>
    </MobileBrowserBlocker>
  );
};

const SuperAdminLayout = () => {
  return (
    <SuperAdminGuard>
      <Outlet />
    </SuperAdminGuard>
  );
};

// Setting up route configuration with improved handling
const DeepLinkRoot = () => {
  useDeepLinking();
  return <Outlet />;
};

export const router = createBrowserRouter([{ element: <DeepLinkRoot />, children: [
  {
    path: "/",
    element: <PublicLayout />,
    children: [
      { path: "login", element: <Login /> },
      { path: "open-app/login", element: <OpenAppPage /> },
      { path: "trial/:token", element: <TrialBookingPage /> },

      { path: "set-password", element: <SetPasswordPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { index: true, errorElement: <NotFound />, element: <Navigate to="/login" replace  /> },
    ]
  },
  {
    path: "/athlete",
    element: <AthleteLayout />,
    children: [
      { index: true, element: <Navigate to="/athlete/dashboard" replace /> },
      { path: "dashboard", element: <AthleteDashboard /> },
      { path: "schedule", element: <AthleteSchedule /> },
      { path: "history", element: <AthleteHistory /> },
      { path: "reservations", element: <AthleteReservations /> },
      { path: "stats", element: <AthleteStats /> },
      { path: "subscriptions", element: <AthleteSubscriptions /> },
      { path: "profile", element: <UserProfile /> },
    ],
  },
  {
    path: "/coach",
    element: <CoachLayout />,
    children: [
      { index: true, element: <Navigate to="/coach/dashboard" replace /> },
      { path: "schedule", element: <Schedule /> },
      { path: "profile", element: <UserProfile /> },
      { path: "dashboard", element: <CoachDashboard /> },
      { path: "statistics", element: <CoachPerformanceDashboard /> },
      // Même page que /admin/trials : le gating par rôle (conversion sans
      // abonnement, pas de suppression) est interne à TrialsPage.
      { path: "trials", element: <TrialsPage /> },
    ],
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: "dashboard", element: <Dashboard /> },
      { path: "clients", element: <Clients /> },
      { path: "clients/import", element: <ClientsImport /> },
      { path: "clients/:id", element: <ClientDetails /> },
      { path: "coaches", element: <Coaches /> },
      { path: "coaches/:id", element: <CoachDetails /> },
      { path: "classes", element: <Classes /> },
      { path: "schedule", element: <Schedule /> },
      { path: "calendars", element: <CalendarsPage /> },
      { path: "inventory", element: <Inventory /> },
      { path: "attendance", element: <Attendance /> },
      { path: "settings", element: <Settings /> },
      { path: "payments", element: <Payments /> },
      { path: "ratings", element: <AdminRatingsPage /> },
      { path: "audit", element: <AuditLogPage /> },
      { path: "subscription-types", element: <SubscriptionTypes /> },
      { path: "trials", element: <TrialsPage /> },
      { path: "session-debts", element: <SessionDebts /> },
      { path: "accounting", element: <Accounting /> },
    ],
  },
  {
    path: "/super-admin",
    element: <SuperAdminLayout />,
    children: [
      { index: true, element: <Navigate to="/super-admin/dashboard" replace /> },
      { path: "dashboard", element: <SuperAdminDashboard /> },
      { path: "tenants", element: <SuperAdminTenants /> },
      { path: "admins", element: <SuperAdminAdmins /> },
      { path: "users", element: <SuperAdminUsers /> },
      { path: "activity", element: <SuperAdminActivity /> },
      { path: "health", element: <SuperAdminHealth /> },
      { path: "sessions-audit", element: <SuperAdminSessionsAudit /> },
      { path: "cancellations-review", element: <SuperAdminCancellationsReview /> },
      { path: "app-versions", element: <SuperAdminAppVersions /> },
      { path: "welcome-messages", element: <SuperAdminWelcomeMessages /> },
    ],
  },
  // Explicit 404 route
  {
    path: "/404",
    element: <NotFound />,
  },
  // Catch-all route for any unmatched paths
  {
    path: "*",
    element: <NotFound />,
  },
] }]);
